import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { Loader2, MapPin } from "lucide-react";
import api, { formatPrice, formatApiError } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

// Xəritə (Leaflet ~40 KB) yalnız lazım olanda yüklənir.
const PinMap = lazy(() => import("./PinMap"));

// Heç nə bilinməyəndə xəritənin başlanğıc yeri — mağazanın özü (53 Zərifə
// Əliyeva): müştərilərin çoxu mərkəzdədir, boş xəritə şəhər görünüşündədir.
const BAKU = { lat: 40.37499, lon: 49.85610 };
// Telefonun mövqeyi yazılan ünvana bu qədər yaxındırsa eyni yer sayılır.
// GPS bina içində 20-100 m yanılır, Wolt-un ünvan tanıması da bir qədər.
const NEAR_M = 300;

const distanceM = (a, b) => {
  const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r;
  const dLon = (b.lon - a.lon) * r;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
};
const kmText = (m) => (m / 1000).toFixed(1).replace(".", ",");

/**
 * Çatdırılma ünvanı (təsisçi qərarları, 2026-10-02 və 2026-10-20).
 *
 * KURYER KOORDİNATA GEDİR: Wolt-un sənədinə görə koordinat verilibsə mətn
 * ünvanından ÜSTÜN tutulur. Ona görə koordinat üç yoldan gəlir və hər biri
 * müştəriyə xəritədə GÖRÜNÜR:
 *   1. Wolt yazılan küçəni özü tanıyır → nişan oradadır.
 *   2. Telefonun mövqeyi (icazə ilə) — YALNIZ yazılan ünvana yaxındırsa
 *      avtomatik. Uzaqdırsa SORUŞULUR: işdə oturub ev ünvanını yazan
 *      müştərinin GPS-i kuryeri işə göndərərdi və bunu heç kim görməzdi.
 *   3. Müştəri nişanı xəritədə özü qoyur (icazə verilməyəndə əsas yol).
 *
 * Nişan müştərinin özündəndirsə (2, 3) altında ən yaxın ünvan yazılır
 * («Nişan: … yaxınlığı», serverdən gəlir) — müştəri nişanı düz yerə
 * qoyduğunu görür.
 *
 * İKİ QEYD SAHƏSİ: «mənzil, mərtəbə, giriş, domofon» (haraya) və «kuryerə
 * qeyd» (necə) — kuryerə ikisi birləşib gedir.
 *
 * Mövqe icazəsi ünvan sahəsinə İLK toxunuşda istənir — səhifə açılan kimi
 * soruşmaq refleksli imtina gətirir. Əvvəlcədən verilibsə təkrar soruşulmur.
 *
 * YAZDIQCA ÜNVAN TƏKLİFİ (təsisçi qərarı, 2026-10-21): küçə xanasının
 * altında Photon (OSM) təklifləri (`/address/suggest`, serverdə keş).
 * Bina/yer seçiləndə mətn dolur və nişan həmin nöqtəyə düşür (`osm`);
 * yalnız küçə seçiləndə mətn dolur, nömrəni müştəri davam edib yazır.
 * Təklif yalnız YAZARKƏN gəlir — seçimdən sonra siyahı yenidən açılmır.
 *
 * GPS → ÜNVAN XANASI (təsisçi qərarı, 2026-10-21): mövqe tapılanda küçə
 * xanası BOŞDURSA serverin tapdığı ünvanla («/address/reverse»: 80 m-də
 * bina, yoxsa Nominatim) dolur və nişan GPS nöqtəsinə qoyulur; altında
 * «mövqeyinizə görə yazıldı — səhvdirsə düzəldin» qeydi. Yazılmış ünvanın
 * üstündən YAZILMIR. GPS bina içində 20-100 m yanılır — kuryer nişana
 * gedir, mətn məlumat üçündür.
 *
 * «XƏRİTƏDƏN SEÇ» (təsisçi qərarı, 2026-10-21): əvvəlki kiçik «Mövqeyimi
 * götür» düyməsi əvəzinə xəritənin üstündə gözəçarpan, ikonlu düymə — tam
 * ekran xəritəni açır. Xəritə həmişə görünür (küçə yazılmamış da), tam
 * ekranda «Mövqeyim» düyməsi telefonun yerini NİŞAN edir.
 *
 * `initialPin` — əvvəlki sifarişdən yadda qalan nişan (cihaz və ya hesab):
 * müştəri onu yenidən qoymur. `onPinChange` nişanı yuxarı ötürür ki,
 * sifarişdən sonra yadda saxlanılsın.
 *
 * `wolt` false olanda (telefonla razılaşma) yalnız mətn sahələri qalır.
 */
export const AddressFields = ({ wolt, addr, setAddr, quote, setQuote, nonce, sms, offline,
                                initialPin, onPinChange, needsAlternative, onAlternative }) => {
  const { t } = useLanguage();
  const [geo, setGeo] = useState("idle");      // idle | asking | granted | denied | unavailable
  const [gps, setGps] = useState(null);
  const [pin, setPin] = useState(initialPin || null); // {lat, lon, source: "gps" | "map"}
  const [woltPos, setWoltPos] = useState(null); // Wolt-un küçəni tanıdığı yer
  const [keepTyped, setKeepTyped] = useState(false); // «yazdığım ünvana» seçildi
  const [mapFull, setMapFull] = useState(false);     // tam ekran xəritə açıqdır
  const [sugg, setSugg] = useState({ items: [], open: false, active: -1 });
  const suggReq = useRef(0);
  const typedRef = useRef(false);   // son dəyişiklik yazmaqla olub (seçimlə yox)
  const [gpsFilled, setGpsFilled] = useState(false);  // xana GPS ünvanı ilə dolub
  const reqId = useRef(0);
  const baseCache = useRef(null);              // {street, nonce, quote}
  // Ekranda son tanınmış yer qalır ki, küçə yazılarkən xəritə hər hərfdə
  // yox olub-görünməsin. Xəbərdarlıqlar isə YALNIZ təzə `woltPos`-a baxır.
  const lastShown = useRef(null);

  // Yadda qalan nişan sonradan gələ bilər (hesab ünvanları serverdən yüklənir).
  useEffect(() => {
    if (initialPin) setPin(initialPin);
  }, [initialPin]);

  const pinChangeRef = useRef(onPinChange);
  pinChangeRef.current = onPinChange;
  useEffect(() => {
    if (pinChangeRef.current) pinChangeRef.current(pin);
  }, [pin]);

  const locate = useCallback(() => {
    if (!("geolocation" in navigator)) { setGeo("unavailable"); return; }
    setGeo("asking");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setGps({ lat: Number(p.coords.latitude.toFixed(6)), lon: Number(p.coords.longitude.toFixed(6)) });
        setGeo("granted");
      },
      (e) => setGeo(e.code === 1 ? "denied" : "unavailable"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 120000 },
    );
  }, []);

  // Tam ekran xəritədə «Mövqeyim»: telefonun yeri birbaşa NİŞAN olur —
  // müştəri xəritəni açıb özü basır, yəni «olduğum yerə» qərarı onundur.
  const locateToPin = useCallback(() => {
    if (!("geolocation" in navigator)) { setGeo("unavailable"); return; }
    setGeo("asking");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const here = { lat: Number(p.coords.latitude.toFixed(6)), lon: Number(p.coords.longitude.toFixed(6)) };
        setGps(here);
        setGeo("granted");
        setKeepTyped(false);
        setPin({ ...here, source: "gps" });
      },
      (e) => setGeo(e.code === 1 ? "denied" : "unavailable"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 120000 },
    );
  }, []);

  // Mövqe tapıldı, xana boşdur → ünvan serverdən gəlib xanaya yazılır,
  // nişan GPS nöqtəsinə qoyulur. Xanada mətn varsa toxunulmur.
  useEffect(() => {
    if (!wolt || !gps || addr.street.trim()) return undefined;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/address/reverse", { params: { lat: gps.lat, lon: gps.lon } });
        if (!alive || !data.text) return;
        typedRef.current = false;
        setSugg({ items: [], open: false, active: -1 });
        setAddr((a) => (a.street.trim() ? a : { ...a, street: data.text }));
        setKeepTyped(false);
        setWoltPos(null);
        setPin({ ...gps, source: "gps" });
        setGpsFilled(true);
      } catch {
        /* ünvan tapılmadı — xana boş qalır, müştəri yazır */
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gps]);

  // İcazə əvvəlcədən verilibsə mövqe səssiz götürülür; rədd edilibsə soruşulmur.
  useEffect(() => {
    if (!wolt) return;
    if (!("geolocation" in navigator)) { setGeo("unavailable"); return; }
    navigator.permissions?.query({ name: "geolocation" })
      .then((st) => {
        if (st.state === "granted") locate();
        else if (st.state === "denied") setGeo("denied");
      })
      .catch(() => {});
  }, [wolt, locate]);

  const closeSugg = () => setSugg((x) => (x.open ? { ...x, open: false, active: -1 } : x));

  const pickSuggestion = (item) => {
    typedRef.current = false;
    setGpsFilled(false);
    setSugg({ items: [], open: false, active: -1 });
    setAddr({ ...addr, street: item.text });
    setKeepTyped(false);
    setWoltPos(null);
    if (item.lat != null && item.lon != null) {
      setPin({ lat: item.lat, lon: item.lon, source: "osm" });
    } else {
      setPin((p) => (p && p.source === "map" ? p : null));
    }
  };

  const onStreetKey = (e) => {
    if (!sugg.open || sugg.items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSugg((x) => ({ ...x, active: (x.active + 1) % x.items.length }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSugg((x) => ({ ...x, active: (x.active - 1 + x.items.length) % x.items.length }));
    } else if (e.key === "Enter") {
      if (sugg.active >= 0) { e.preventDefault(); pickSuggestion(sugg.items[sugg.active]); }
    } else if (e.key === "Escape") {
      closeSugg();
    }
  };

  // Yazdıqca təkliflər: 250 ms sonra, 3 hərfdən; son sorğu qalib gəlir.
  useEffect(() => {
    if (!wolt || !typedRef.current) return undefined;
    const q = addr.street.trim();
    if (q.length < 3) { setSugg({ items: [], open: false, active: -1 }); return undefined; }
    const id = ++suggReq.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/address/suggest", { params: { q } });
        if (id !== suggReq.current) return;
        const items = data.items || [];
        setSugg({ items, open: items.length > 0, active: -1 });
      } catch {
        if (id === suggReq.current) setSugg({ items: [], open: false, active: -1 });
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [wolt, addr.street]);

  const onStreet = (value) => {
    typedRef.current = true;
    setGpsFilled(false);
    setAddr({ ...addr, street: value });
    // Yeni küçə: avtomatik (GPS) nişan yenidən qiymətləndirilir. Müştərinin
    // ÖZ qoyduğu nişan qalır — uzaq düşsə aşağıda xəbərdarlıq çıxır.
    setPin((p) => (p && p.source === "map" ? p : null));
    setKeepTyped(false);
    setWoltPos(null);
  };

  /* Qiymət. Nişan varsa qiymət O NÖQTƏYƏ görə soruşulur, yoxdursa küçəyə görə.
     Küçənin öz tanınması (`woltPos`) hər küçə üçün bir dəfə soruşulur —
     həm nişanın küçədən uzaq olub-olmadığını bilmək, həm də nişanı ilk
     dəfə qoymaq üçün. Son sorğu qalib gəlir (`reqId`). */
  useEffect(() => {
    if (!wolt) return undefined;
    const street = addr.street.trim();
    if (street.length < 5) {
      setQuote({ status: "idle" });
      setWoltPos(null);
      return undefined;
    }
    const id = ++reqId.current;
    let alive = true;
    const current = () => alive && id === reqId.current;
    setQuote({ status: "loading" });
    const timer = setTimeout(async () => {
      const post = (extra) =>
        api.post("/delivery/quote", { street, city: "Bakı", ...extra }).then((r) => r.data);
      const show = (q) => setQuote(q.available
        ? { status: "ready", ...q }
        : { status: "unavailable", kind: q.kind, message: q.message });
      try {
        let base = baseCache.current;
        if (!base || base.street !== street || base.nonce !== nonce) {
          const q = await post({});
          if (!current()) return;
          base = { street, nonce, quote: q };
          baseCache.current = base;
          setWoltPos(q.available && q.lat != null ? { lat: q.lat, lon: q.lon } : null);
          if (!q.available) { show(q); return; }
        }
        if (pin) {
          const q = await post({ lat: pin.lat, lon: pin.lon, source: pin.source });
          if (current()) show(q);
          return;
        }
        const wp = base.quote.lat != null ? { lat: base.quote.lat, lon: base.quote.lon } : null;
        if (gps && wp && !keepTyped && distanceM(gps, wp) <= NEAR_M) {
          setPin({ ...gps, source: "gps" });  // effekt nişanla yenidən işləyir
          return;
        }
        show(base.quote);
      } catch (err) {
        if (current()) setQuote({ status: "error", message: formatApiError(err.response?.data?.detail) });
      }
    }, 900);
    return () => { alive = false; clearTimeout(timer); };
  }, [wolt, addr.street, pin, gps, keepTyped, nonce, setQuote]);

  const streetReady = addr.street.trim().length >= 5;
  const gpsFar = wolt && gps && woltPos && !pin && !keepTyped && distanceM(gps, woltPos) > NEAR_M;
  const pinFar = pin && pin.source === "map" && woltPos && distanceM(pin, woltPos) > NEAR_M;
  const noGeo = geo === "denied" || geo === "unavailable";
  if (woltPos) lastShown.current = woltPos;
  const mapPos = pin
    || (streetReady && (woltPos || lastShown.current))
    || (geo === "granted" && gps)
    || BAKU;
  // Nişan müştərinin özündəndirsə, altında ən yaxın ünvan (serverdən).
  const pinLabel = pin && quote.status === "ready" && quote.pin_label ? quote.pin_label : "";

  const field = "w-full border border-brand-line bg-white px-4 py-3 text-sm outline-none focus:border-brand-ink transition-colors";
  const labelCls = "overline block mb-2";

  return (
    <>
      <div className="grid grid-cols-1 gap-4 mt-5">
        <div>
          <div className="flex items-end justify-between gap-3 mb-2">
            <label className="overline block">{t("address_street")}{wolt ? " *" : ""}</label>
            {wolt && geo === "asking" && (
              <span className="flex items-center gap-1 text-xs text-brand-muted">
                <Loader2 size={12} className="animate-spin" /> {t("geo_asking")}
              </span>
            )}
          </div>
          <div className="relative">
            <input
              className={field}
              value={addr.street}
              maxLength={200}
              autoComplete="off"
              onFocus={() => { if (wolt && geo === "idle") locate(); }}
              onBlur={() => setTimeout(closeSugg, 150)}
              onKeyDown={onStreetKey}
              onChange={(e) => onStreet(e.target.value)}
              placeholder={t("address_street_ph")}
              data-testid="checkout-street"
              role="combobox"
              aria-expanded={sugg.open}
              aria-controls="street-suggest"
              aria-autocomplete="list"
            />
            {wolt && sugg.open && sugg.items.length > 0 && (
              <ul id="street-suggest" role="listbox" data-testid="street-suggest"
                  className="absolute left-0 right-0 top-full z-20 bg-white border border-brand-line shadow-md max-h-72 overflow-auto">
                {sugg.items.map((item, i) => (
                  <li key={item.label} role="option" aria-selected={i === sugg.active}
                      data-testid={`street-suggest-${item.kind}`}
                      onMouseDown={(e) => { e.preventDefault(); pickSuggestion(item); }}
                      onMouseEnter={() => setSugg((x) => ({ ...x, active: i }))}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm cursor-pointer ${i === sugg.active ? "bg-brand-bg2" : ""}`}>
                    <MapPin size={14} strokeWidth={1.5} className="shrink-0 text-brand-muted" />
                    <span className="truncate">{item.label}</span>
                  </li>
                ))}
                <li className="px-4 py-1 text-[10px] text-brand-muted border-t border-brand-line" aria-hidden="true">
                  © OpenStreetMap
                </li>
              </ul>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t("address_flat")}</label>
            <input
              className={field}
              value={addr.flat || ""}
              maxLength={120}
              onChange={(e) => setAddr({ ...addr, flat: e.target.value })}
              placeholder={t("address_flat_ph")}
              data-testid="checkout-address-flat"
            />
          </div>
          <div>
            <label className={labelCls}>{t("address_note")}</label>
            <input
              className={field}
              value={addr.note}
              maxLength={300}
              onChange={(e) => setAddr({ ...addr, note: e.target.value })}
              placeholder={t("address_note_ph")}
              data-testid="checkout-address-note"
            />
          </div>
        </div>
      </div>

      {wolt && (
        <div className="mt-4">
          <button type="button" onClick={() => setMapFull(true)} data-testid="map-pick"
            className="mb-2 w-full sm:w-auto flex items-center justify-center gap-2 border border-brand-ink bg-white px-5 py-3 text-sm font-medium tracking-wide hover:bg-brand-ink hover:text-white transition-colors">
            <MapPin size={17} strokeWidth={1.75} /> {t("map_pick")}
          </button>
          <Suspense fallback={<div className="h-56 w-full border border-brand-line bg-brand-bg2" />}>
            <PinMap
              lat={mapPos.lat}
              lon={mapPos.lon}
              zoom={pin || woltPos || gps ? 17 : 12}
              onMove={(lat, lon) => setPin({ lat, lon, source: "map" })}
              full={mapFull}
              onFull={setMapFull}
              onLocate={locateToPin}
              locating={geo === "asking"}
            />
          </Suspense>

          {gpsFar ? (
            /* Telefon başqa yerdədir — kuryer hara getsin, müştəri özü deyir. */
            <div className="mt-2 border border-brand-line bg-white p-3 text-xs" data-testid="geo-far">
              <p>{t("geo_far").replace("{km}", kmText(distanceM(gps, woltPos)))}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <button type="button" onClick={() => setKeepTyped(true)} data-testid="geo-far-typed"
                  className="border border-brand-ink px-3 py-1.5">{t("geo_far_typed")}</button>
                <button type="button" onClick={() => setPin({ ...gps, source: "gps" })} data-testid="geo-far-here"
                  className="border border-brand-line px-3 py-1.5">{t("geo_far_here")}</button>
              </div>
            </div>
          ) : pinFar ? (
            <div className="mt-2 border border-destructive/40 bg-white p-3 text-xs" data-testid="pin-far">
              <p className="text-destructive">{t("pin_far").replace("{km}", kmText(distanceM(pin, woltPos)))}</p>
              {pinLabel && <p className="mt-1 text-brand-sub">{t("pin_near").replace("{place}", pinLabel)}</p>}
              <button type="button" onClick={() => { setPin(null); setKeepTyped(true); }}
                data-testid="pin-far-reset" className="mt-2 border border-brand-line px-3 py-1.5">
                {t("pin_far_reset")}
              </button>
            </div>
          ) : (
            <p className="text-xs text-brand-muted mt-2" data-testid="map-hint">
              {pin?.source === "gps" ? `✓ ${t("geo_used")}`
                : pin?.source === "map" ? `✓ ${t("map_pin_set")}`
                : pin?.source === "osm" ? `✓ ${t("osm_pin_set")}`
                : noGeo ? t("geo_denied")
                : t("map_hint")}
              {gpsFilled && pin?.source === "gps" && (
                <span className="block mt-0.5 text-brand-sub" data-testid="geo-filled">
                  {t("geo_filled")}
                </span>
              )}
              {pinLabel && (
                <span className="block mt-0.5 text-brand-sub" data-testid="pin-label">
                  {t("pin_near").replace("{place}", pinLabel)}
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {wolt && quote.status !== "idle" && (
        <div className="mt-4 text-sm" data-testid="quote-box" aria-live="polite">
          {quote.status === "loading" && (
            <p className="flex items-center gap-2 text-brand-muted">
              <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
              {t("quote_loading")}
            </p>
          )}
          {quote.status === "ready" && (
            <div className="border border-brand-line bg-white p-4 space-y-1.5">
              <p className="flex justify-between">
                <span>{t("delivery_fee_label")}</span>
                <span className="font-medium" data-testid="quote-fee">{formatPrice(quote.fee)}</span>
              </p>
              {/* Wolt ünvanı özü tanıyır və «həmişə nəsə tapır» — səhv
                  tanınmış ünvanı yalnız müştəri görə bilər. */}
              <p className="text-xs text-brand-muted" data-testid="quote-address">
                {t("quote_recognized")}:{" "}
                <span className="text-brand-ink">{quote.formatted_address}</span>.{" "}
                {t("quote_check_hint")}
              </p>
              {/* Mağaza «bağlı» edibsə vəd verilmir — sahibkar zəng edib vaxtı
                  deyəcək (təsisçi mətni, 2026-10-20). */}
              <p className="text-xs text-brand-muted" data-testid="quote-note">
                {offline ? t("delivery_offline_note")
                  : quote.scheduled ? t("quote_scheduled_note") : t("quote_dispatch_note")}
                {sms && !offline && ` ${t("quote_sms_note")}`}
              </p>
            </div>
          )}
          {(quote.status === "unavailable" || quote.status === "error") && (
            <div className="border border-destructive/40 bg-white p-4" data-testid="quote-unavailable">
              <p className="text-destructive">{quote.message}</p>
              {needsAlternative && (
                <>
                  <p className="text-xs text-brand-muted mt-1.5">{t("quote_alternatives")}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button type="button" onClick={() => onAlternative("manual")} data-testid="alt-manual"
                      className="border border-brand-line px-3 py-2 text-xs hover:border-brand-ink">
                      {t("delivery_manual")}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
